import React from 'react';
import {
    Box,
    Button,
    Flex,
    Text,
    VStack,
    HStack,
    Heading,
    Image,
    Badge,
    Select,
    FormControl,
    FormLabel,
    Input,
    Checkbox,
    Icon,
    SimpleGrid,
    Grid,
    Progress,
    Stat,
    StatLabel,
    StatNumber,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Divider,
    useToast,
    RadioGroup,
    Radio,
    Stack,
    Link,
} from '@chakra-ui/react';
import { FaCoins, FaTicketAlt, FaRegClock, FaFire, FaSearch, FaChevronDown, FaChevronUp, FaCheck } from 'react-icons/fa';

// Utility function to format large numbers
const formatNumber = (num) => {
    if (!num) return "0";
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'k';
    }
    return num;
};

// Calculate raffle fee (5%)
const calculateRaffleFee = (ticketPrice, totalTickets) => {
    return (ticketPrice * totalTickets) * 0.05;
};

// Calculate raffle earnings
const calculateRaffleEarnings = (ticketPrice, totalTickets) => {
    const totalRevenue = ticketPrice * totalTickets;
    const fee = calculateRaffleFee(ticketPrice, totalTickets);
    return totalRevenue - fee;
};

const NewRaffleTab = ({
    raffleAssetType = 'nft',
    setRaffleAssetType = () => {},
    selectedRaffleNft = '',
    setSelectedRaffleNft = () => {},
    nftData = {},
    selectedTokenSymbol = 'JINU',
    setSelectedTokenSymbol = () => {},
    selectedTokenAmount = 1000,
    setSelectedTokenAmount = () => {},
    selectedTokenAddress = '',
    setSelectedTokenAddress = () => {},
    raffleTicketPrice = 100,
    setRaffleTicketPrice = () => {},
    raffleTotalTickets = 50,
    setRaffleTotalTickets = () => {},
    raffleDuration = 7,
    setRaffleDuration = () => {},
    handleCreateRaffle = () => {},
    topTokens = [
        { symbol: 'JINU', name: 'JINU Token', image: '/images/token-placeholder.png', price: 1 },
        { symbol: 'SOL', name: 'Solana', image: '/images/solana-logo.png', price: 172.45 },
        { symbol: 'BONK', name: 'Bonk', image: '/images/bonk-logo.png', price: 0.00002 },
        { symbol: 'JUP', name: 'Jupiter', image: '/images/jupiter-logo.png', price: 0.85 }
    ],
    searchTokens = [],
    setSearchTokens = () => {},
    tokenSearchQuery = '',
    setTokenSearchQuery = () => {},
    handleTokenSearch = () => {},
}) => {
    const toast = useToast();

    // Handle asset type change
    const handleAssetTypeChange = (value) => {
        setRaffleAssetType(value);
        // Reset selections when changing asset type
        if (value === 'nft') {
            setSelectedTokenAmount('');
            setSelectedTokenAddress('');
            setSelectedTokenSymbol('');
        } else {
            setSelectedRaffleNft('');
        }
    };

    // Handle token selection
    const handleTokenSelect = (token) => {
        setSelectedTokenAddress(token.address);
        setSelectedTokenSymbol(token.symbol);
        setSearchTokens([]);
        setTokenSearchQuery('');
    };

    return (
        <Box 
            p={0} 
            borderRadius="lg" 
            bg="gray.900" 
            border="1px solid" 
            borderColor="#3182CE" 
            overflow="hidden" 
            boxShadow="0 4px 20px rgba(49, 130, 206, 0.25)"
        >

            
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} p={4}>
                {/* Left Side - Preview */}
                <Box 
                    bg="gray.900" 
                    p={6} 
                    position="relative"
                    height="auto"
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    borderRadius="lg"
                >
                    <Heading size="md" mb={6} color="#3182CE">Raffle Preview</Heading>
                    
                    {/* Raffle Card Preview - FIXED SIZE */}
                    <Box 
                        width="300px"
                        borderWidth="1px"
                        borderRadius="lg"
                        overflow="hidden"
                        bg="gray.800"
                        borderColor="#3182CE"
                        boxShadow="0 4px 12px rgba(49, 130, 206, 0.3)"
                        mb={6}
                        display="flex"
                        flexDirection="column"
                    >
                        <Box position="relative" height="220px" flexShrink={0}>
                            {raffleAssetType === 'nft' && selectedRaffleNft ? (
                                <Image
                                    src={nftData?.[selectedRaffleNft]?.image || "https://via.placeholder.com/300"}
                                    alt={selectedRaffleNft}
                                    w="100%"
                                    h="100%"
                                    objectFit="cover"
                                />
                            ) : (
                                <Flex
                                    w="100%"
                                    h="100%"
                                    bg="gray.900"
                                    justifyContent="center"
                                    alignItems="center"
                                    position="relative"
                                >

                                    <VStack spacing={2}>
                                        <Icon as={FaCoins} boxSize="50px" color="#3182CE" />
                                        <Text fontWeight="bold" fontSize="xl" color="white" maxW="280px" textAlign="center" isTruncated>
                                            {selectedTokenAmount ? formatNumber(selectedTokenAmount) : "0"} {selectedTokenSymbol || "JINU"}
                                        </Text>
                                    </VStack>
                                </Flex>
                            )}
                            
                            {/* Hot Tag */}
                            <Badge 
                                position="absolute" 
                                top={2} 
                                right={2} 
                                colorScheme="blue"
                                display="flex"
                                alignItems="center"
                                px={2}
                                py={1}
                                borderRadius="md"
                                zIndex={1}
                            >
                                <Icon as={FaFire} mr={1} /> New Raffle
                            </Badge>
                        </Box>
                        
                        <Box p={5} flex="1" display="flex" flexDirection="column">
                            <Heading size="md" mb={2} noOfLines={1} isTruncated maxW="100%">
                                {raffleAssetType === 'nft' ? (selectedRaffleNft || "Select an NFT") : `${selectedTokenAmount ? formatNumber(selectedTokenAmount) : "0"} ${selectedTokenSymbol || "JINU"} Raffle`}
                            </Heading>
                            
                            <Text color="gray.400" fontSize="sm" mb={3} isTruncated>
                                Created by You • {new Date().toLocaleDateString()}
                            </Text>
                            
                            <HStack justify="space-between" mb={3} spacing={2}>
                                <Badge colorScheme="#ff0000" px={2} py={1} borderRadius="md" ml={0} mr={1}>
                                    <HStack spacing={1}>
                                        <Icon as={FaTicketAlt} />
                                        <Text>{raffleTotalTickets || "0"} tickets</Text>
                                    </HStack>
                                </Badge>
                                
                                <Badge colorScheme="#ff0000" px={2} py={1} borderRadius="md" ml={1} mr={0}>
                                    <HStack spacing={1}>
                                        <Icon as={FaCoins} />
                                        <Text>{raffleTicketPrice || "0"} JINU</Text>
                                    </HStack>
                                </Badge>
                            </HStack>
                            
                            <Progress 
                                value={0} 
                                colorScheme="#ff0000" 
                                size="sm" 
                                borderRadius="full" 
                                mb={3}
                            />
                            
                            <Flex justify="space-between" align="center" mb={2} mt="auto">
                                <HStack>
                                    <Icon as={FaRegClock} color="gray.400" />
                                    <Text fontSize="sm">
                                        Ends in {raffleDuration || "7"} days
                                    </Text>
                                </HStack>
                                <Text fontSize="sm" fontWeight="bold" color="#3182CE" isTruncated maxW="120px" textAlign="right">
                                    {raffleAssetType === 'nft' ? `Value: ${nftData?.[selectedRaffleNft]?.price ? formatNumber(nftData[selectedRaffleNft].price) : "0"} JINU` : `Value: ${selectedTokenAmount ? formatNumber(selectedTokenAmount) : "0"} JINU`}
                                </Text>
                            </Flex>
                        </Box>
                    </Box>
                    
                    {/* Raffle Stats */}
                    <Box 
                        w="100%" 
                        p={4} 
                        borderRadius="md" 
                        bg="whiteAlpha.100"
                        borderColor="#3182CE"
                        borderWidth="1px"
                    >
                        <Heading size="sm" mb={3} color="#3182CE">Raffle Summary</Heading>
                        
                        <SimpleGrid columns={2} spacing={4}>
                            <Stat>
                                <StatLabel color="gray.400">Asset Value</StatLabel>
                                <StatNumber fontSize="md" color="#3182CE">
                                    {raffleAssetType === 'nft' ? formatNumber(nftData?.[selectedRaffleNft]?.price || 0) : selectedTokenAmount} JINU
                                </StatNumber>
                            </Stat>
                            
                            <Stat>
                                <StatLabel color="gray.400">Potential Revenue</StatLabel>
                                <StatNumber fontSize="md" color="#3182CE">
                                    {(raffleTicketPrice * raffleTotalTickets).toFixed(2) || "0"} JINU
                                </StatNumber>
                            </Stat>
                            
                            <Stat>
                                <StatLabel color="gray.400">Platform Fee (5%)</StatLabel>
                                <StatNumber fontSize="md" color="#3182CE">
                                    {formatNumber(calculateRaffleFee(raffleTicketPrice, raffleTotalTickets))} JINU
                                </StatNumber>
                            </Stat>
                            
                            <Stat>
                                <StatLabel color="gray.400">Your Earnings</StatLabel>
                                <StatNumber fontSize="md" color="#3182CE">
                                    {formatNumber(calculateRaffleEarnings(raffleTicketPrice, raffleTotalTickets))} JINU
                                </StatNumber>
                            </Stat>
                        </SimpleGrid>
                    </Box>
                </Box>
                
                {/* Right Side - Options */}
                <Box 
                    bg="rgba(45, 55, 72, 0.7)" 
                    p={6} 
                    height="auto" 
                    borderRadius="lg"
                >
                    <Heading size="md" mb={6} color="#3182CE">Configure Your Raffle</Heading>
                    
                    {/* Asset Type Selection */}
                    <FormControl mb={4}>
                        <FormLabel color="gray.300">Asset Type</FormLabel>
                        <RadioGroup onChange={(value) => setRaffleAssetType(value)} value={raffleAssetType} colorScheme="blue">
                            <Stack direction="row">
                                <Radio value="nft">NFT</Radio>
                                <Radio value="token">Token</Radio>
                            </Stack>
                        </RadioGroup>
                    </FormControl>
                    
                    {/* NFT Selection */}
                    {raffleAssetType === 'nft' && (
                        <FormControl mb={4}>
                            <FormLabel color="gray.300">Select NFT to Raffle</FormLabel>
                            <Select 
                                placeholder="Choose an NFT" 
                                bg="gray.800"
                                color="white"
                                borderColor="blue.400"
                                value={selectedRaffleNft}
                                onChange={(e) => setSelectedRaffleNft(e.target.value)}
                            >
                                {Object.keys(nftData || {}).length > 0 ? (
                                    Object.keys(nftData).map(nftId => (
                                        <option key={nftId} value={nftId}>
                                            {nftData[nftId].name}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No NFTs available</option>
                                )}
                            </Select>
                        </FormControl>
                    )}
                    
                    {/* Token Selection */}
                    {raffleAssetType === 'token' && (
                        <>
                            <FormControl mb={4}>
                                <FormLabel color="gray.300">Select Token</FormLabel>
                                <InputGroup>
                                    <Input
                                        placeholder="Search tokens..."
                                        bg="gray.800"
                                        color="white"
                                        borderColor="blue.400"
                                        value={tokenSearchQuery || ''}
                                        onChange={(e) => {
                                            setTokenSearchQuery(e.target.value);
                                            if (handleTokenSearch) {
                                                handleTokenSearch(e.target.value);
                                            }
                                        }}
                                    />
                                    <InputRightElement>
                                        <Icon as={FaSearch} color="gray.500" />
                                    </InputRightElement>
                                </InputGroup>
                                
                                {searchTokens && searchTokens.length > 0 && tokenSearchQuery && (
                                    <Box 
                                        position="absolute" 
                                        zIndex="dropdown"
                                        mt={1}
                                        w="100%"
                                        bg="gray.800"
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor="blue.400"
                                        maxH="200px"
                                        overflowY="auto"
                                        boxShadow="lg"
                                    >
                                        {searchTokens.map(token => (
                                            <Flex
                                                key={token.symbol}
                                                p={2}
                                                align="center"
                                                _hover={{ bg: 'gray.700' }}
                                                cursor="pointer"
                                                onClick={() => {
                                                    setSelectedTokenSymbol(token.symbol);
                                                    setTokenSearchQuery('');
                                                    if (setSearchTokens) {
                                                        setSearchTokens([]);
                                                    }
                                                }}
                                            >
                                                <Image 
                                                    src={token.image} 
                                                    alt={token.name} 
                                                    boxSize="24px" 
                                                    mr={2} 
                                                    borderRadius="full" 
                                                />
                                                <Text color="white">{token.name} ({token.symbol})</Text>
                                            </Flex>
                                        ))}
                                    </Box>
                                )}
                                
                                {(!searchTokens || searchTokens.length === 0) && (
                                    <Box mt={2}>
                                        <Text fontSize="sm" color="gray.400">Popular tokens:</Text>
                                        <Flex mt={2} wrap="wrap" gap={2}>
                                            {topTokens && topTokens.map(token => (
                                                <Badge 
                                                    key={token.symbol}
                                                    colorScheme="blue"
                                                    p={2}
                                                    borderRadius="md"
                                                    cursor="pointer"
                                                    onClick={() => {
                                                        setSelectedTokenSymbol(token.symbol);
                                                    }}
                                                    bg={selectedTokenSymbol === token.symbol ? 'blue.500' : 'blue.800'}
                                                >
                                                    {token.symbol}
                                                </Badge>
                                            ))}
                                        </Flex>
                                    </Box>
                                )}
                            </FormControl>
                            
                            <FormControl mb={4}>
                                <FormLabel color="gray.300">Token Amount</FormLabel>
                                <NumberInput
                                    min={10}
                                    max={1000000}
                                    value={selectedTokenAmount}
                                    onChange={(valueString) => {
                                        if (setSelectedTokenAmount) {
                                            setSelectedTokenAmount(parseFloat(valueString));
                                        }
                                    }}
                                    bg="gray.800"
                                    borderColor="blue.400"
                                >
                                    <NumberInputField 
                                        bg="gray.700"
                                        color="white"
                                        borderColor="#3182CE"
                                        _hover={{ borderColor: "#3182CE" }}
                                        _focus={{ borderColor: "#3182CE", boxShadow: "0 0 0 1px #3182CE" }}
                                    />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper color="blue.400" />
                                        <NumberDecrementStepper color="blue.400" />
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>
                        </>
                    )}
                    
                    {/* Ticket Price */}
                    <FormControl mb={4}>
                        <FormLabel color="gray.300">Ticket Price (JINU)</FormLabel>
                        <NumberInput
                            min={10}
                            max={10000}
                            value={raffleTicketPrice}
                            onChange={(valueString) => {
                                if (setRaffleTicketPrice) {
                                    setRaffleTicketPrice(parseFloat(valueString));
                                }
                            }}
                            bg="gray.800"
                            borderColor="blue.400"
                        >
                            <NumberInputField 
                                bg="gray.700"
                                color="white"
                                borderColor="#3182CE"
                                _hover={{ borderColor: "#3182CE" }}
                                _focus={{ borderColor: "#3182CE", boxShadow: "0 0 0 1px #3182CE" }}
                            />
                            <NumberInputStepper>
                                <NumberIncrementStepper color="blue.400" />
                                <NumberDecrementStepper color="blue.400" />
                            </NumberInputStepper>
                        </NumberInput>
                    </FormControl>
                    
                    {/* Total Tickets */}
                    <FormControl mb={4}>
                        <FormLabel color="gray.300">Total Tickets</FormLabel>
                        <NumberInput
                            min={10}
                            max={1000}
                            value={raffleTotalTickets}
                            onChange={(valueString) => {
                                if (setRaffleTotalTickets) {
                                    setRaffleTotalTickets(parseInt(valueString));
                                }
                            }}
                            bg="gray.800"
                            borderColor="blue.400"
                        >
                            <NumberInputField 
                                bg="gray.700"
                                color="white"
                                borderColor="#3182CE"
                                _hover={{ borderColor: "#3182CE" }}
                                _focus={{ borderColor: "#3182CE", boxShadow: "0 0 0 1px #3182CE" }}
                            />
                            <NumberInputStepper>
                                <NumberIncrementStepper color="blue.400" />
                                <NumberDecrementStepper color="blue.400" />
                            </NumberInputStepper>
                        </NumberInput>
                    </FormControl>
                    
                    {/* Duration */}
                    <FormControl mb={4}>
                        <FormLabel color="gray.300">Duration (Days)</FormLabel>
                        <NumberInput
                            min={1}
                            max={30}
                            value={raffleDuration}
                            onChange={(valueString) => {
                                if (setRaffleDuration) {
                                    setRaffleDuration(parseInt(valueString));
                                }
                            }}
                            bg="gray.800"
                            borderColor="blue.400"
                        >
                            <NumberInputField 
                                bg="gray.700"
                                color="white"
                                borderColor="#3182CE"
                                _hover={{ borderColor: "#3182CE" }}
                                _focus={{ borderColor: "#3182CE", boxShadow: "0 0 0 1px #3182CE" }}
                            />
                            <NumberInputStepper>
                                <NumberIncrementStepper color="blue.400" />
                                <NumberDecrementStepper color="blue.400" />
                            </NumberInputStepper>
                        </NumberInput>
                    </FormControl>
                    
                    {/* Terms and Conditions */}
                    <FormControl mb={3}>
                        <FormLabel color="gray.300">Terms & Conditions</FormLabel>
                        <Checkbox colorScheme="blue" defaultChecked>
                            <Text fontSize="sm" color="gray.400">
                                I agree to the <Link color="blue.400" href="#">Terms & Conditions</Link> and understand that once created, this raffle cannot be canceled.
                            </Text>
                        </Checkbox>
                    </FormControl>
                    
                    {/* Create Button */}
                    <Button 
                        colorScheme="blue" 
                        size="lg" 
                        w="100%"
                        mt={3}
                        onClick={handleCreateRaffle}
                        isDisabled={
                            (raffleAssetType === 'nft' && !selectedRaffleNft) || 
                            (raffleAssetType === 'token' && (!selectedTokenAmount || !selectedTokenAddress)) ||
                            !raffleTicketPrice || 
                            !raffleTotalTickets
                        }
                        leftIcon={<Icon as={FaTicketAlt} />}
                    >
                        Create Raffle
                    </Button>
                    

                </Box>
            </Grid>
        </Box>
    );
};

export default NewRaffleTab;
